import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { apiErr } from "@/lib/api";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(username, password);
      if (u.role === "client") { setError("This is an internal staff-only system. Client accounts cannot sign in."); setLoading(false); return; }
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      nav("/admin");
    } catch (err) {
      setError(apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#123a63] to-[#3a1d6e]">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[hsl(var(--brand-blue))] opacity-25 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-[hsl(var(--brand-orange))] opacity-20 blur-3xl" />
        <img src="/logo.png" alt="" aria-hidden="true" className="pointer-events-none select-none absolute -right-16 bottom-10 w-[30rem] max-w-none opacity-[0.10] rotate-6" />
        <Link to="/login" className="relative"><Brand light /></Link>
        <div className="relative">
          <h2 className="font-head font-extrabold text-4xl leading-tight mb-4">The operating system for your immigration practice.</h2>
          <p className="text-slate-300 max-w-md">Leads, clients, cases, documents, appointments and billing — one secure internal CRM for the Chahal Overseas team.</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-200"><ShieldCheck className="h-4 w-4 text-[hsl(var(--brand-orange))]" /> Bank-grade security · RCIC-IRCC Certified</div>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 Chahal Overseas Immigration</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md animate-fadeup">
          <div className="lg:hidden mb-8"><Link to="/login"><Brand /></Link></div>
          <h1 className="font-head font-extrabold text-3xl text-slate-900 mb-2">Staff sign in</h1>
          <p className="text-slate-500 mb-8">Sign in to the Chahal Overseas CRM.</p>

          {error && <div className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-xl px-4 py-3" data-testid="login-error">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Username</label>
              <input data-testid="login-username" type="text" autoCapitalize="none" autoCorrect="off" required value={username} onChange={(e) => setUsername(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" placeholder="your username" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" placeholder="••••••••" />
            </div>
            <button data-testid="login-submit" disabled={loading} className="w-full bg-primary text-white py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500" data-testid="login-help">
            Forgot your password? Contact your administrator to have it reset.
          </div>
        </div>
      </div>
    </div>
  );
}
